import { useState } from 'react';
import { Share2, Copy, Check, Download, Mail, MessageCircle, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

export function ShareCollectionCard({ photos, galleryName, shareToken, onDownloadAll }) {
  const [copied, setCopied] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Generate a unique collection URL with photo IDs
  const getCollectionUrl = () => {
    const photoIds = photos.map(p => p.id).join(',');
    const baseUrl = window.location.origin;
    return `${baseUrl}/share/${shareToken}?collection=${encodeURIComponent(photoIds)}`;
  };

  const collectionUrl = getCollectionUrl();
  const shareText = `Check out my ${photos.length} photo${photos.length > 1 ? 's' : ''} from ${galleryName}!`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(collectionUrl);
      setCopied(true);
      toast.success('Collection link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = collectionUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success('Collection link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      setIsSharing(true);
      try {
        await navigator.share({
          title: `My Photos from ${galleryName}`,
          text: shareText,
          url: collectionUrl,
        });
        toast.success('Shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      } finally {
        setIsSharing(false);
      }
    } else {
      handleCopyLink();
    }
  };

  const handleWhatsAppShare = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + collectionUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`My Photos from ${galleryName}`);
    const body = encodeURIComponent(`${shareText}\n\nView my photos here:\n${collectionUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Card className="p-6 bg-gradient-to-br from-[#BF212F]/5 to-[#F06543]/5 border-[#BF212F]/20 dark:bg-gradient-to-br dark:from-[#BF212F]/10 dark:to-[#F06543]/10 dark:border-[#BF212F]/30">
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-2">
          <Share2 className="w-6 h-6 text-[#BF212F]" />
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
            Share Your Photos
          </h3>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-300">
          Found {photos.length} photo{photos.length > 1 ? 's' : ''} of you! Share them with friends and family.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {/* Main Share Button */}
          <Button
            onClick={handleNativeShare}
            disabled={isSharing}
            className="bg-[#BF212F] hover:bg-[#8B1923] text-white"
          >
            <Share2 className="w-4 h-4 mr-2" />
            {isSharing ? 'Sharing...' : 'Share Collection'}
          </Button>

          {/* WhatsApp */}
          <Button
            variant="outline"
            size="icon"
            onClick={handleWhatsAppShare}
            className="bg-[#25D366] hover:bg-[#128C7E] border-[#25D366] text-white"
            title="Share via WhatsApp"
          >
            <MessageCircle className="w-5 h-5" />
          </Button>

          {/* Email */}
          <Button
            variant="outline"
            size="icon"
            onClick={handleEmailShare}
            className="bg-slate-600 hover:bg-slate-700 border-slate-600 text-white"
            title="Share via Email"
          >
            <Mail className="w-5 h-5" />
          </Button>

          {/* Copy Link */}
          <Button
            variant="outline"
            size="icon"
            onClick={handleCopyLink}
            className="dark:border-slate-600"
            title="Copy link"
          >
            {copied ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <Link2 className="w-5 h-5" />
            )}
          </Button>

          {/* Download All */}
          {onDownloadAll && (
            <Button
              variant="outline"
              onClick={onDownloadAll}
              className="dark:border-slate-600"
            >
              <Download className="w-4 h-4 mr-2" />
              Download All
            </Button>
          )}
        </div>

        {/* URL Preview */}
        <div className="mt-3 p-2 bg-white/50 dark:bg-slate-800/50 rounded-lg text-xs text-slate-500 dark:text-slate-400 truncate max-w-md mx-auto">
          {collectionUrl}
        </div>
      </div>
    </Card>
  );
}

export default ShareCollectionCard;
